import { useEffect, useState, Fragment, useCallback } from 'react';
import TablePagination from '@material-ui/core/TablePagination';
import ModalContainer from 'components/molecules/ModalContainer';

// Material UI
import {
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  CircularProgress
} from '@material-ui/core';

// Img
import productImage from 'assets/img/productDefault.png';

// Styles
import useStyles from './IMProductsList.styles';

// Components
import useProductsIM from 'hooks/useProductsIM';
import IMInventoryEntryModal from 'components/organisms/IMInventoryEntryModal';
import IMAdjustmentModal from 'components/organisms/IMAdjustmentModal';
import IMInventoryMenu from 'components/organisms/IMInventoryMenu';
import { IProductList, IProducts, Variation } from 'interfaces/imProducts.interface';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es';
import { download } from 'utils/downloadXlsx';

interface Props {
  productsList: IProductList;
  page: number;
  setPage: (state: number) => void;
  setParentId: (state: number) => void;
  parentId: number;
  rowsPerPage: number;
  setRowsPerPage: (state: number) => void;
  setProductsList: (state: IProductList) => void;
  handleOnClickEditProduct: (wareHouseId: string) => void;
  getAllProducts(): void;
}

export default function IMProductsList({
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  productsList,
  setProductsList,
  setParentId,
  parentId,
  handleOnClickEditProduct,
  getAllProducts
}: Props) {
  const [openAdjustment, setOpenAdjustment] = useState(false);
  const [openEnterProduct, setOpenEnterProduct] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState<number>(0);
  const [searchData, setSearchData] = useState<{
    sku: string;
    fecha: MaterialUiPickersDate;
  }>({
    sku: '',
    fecha: new Date()
  });
  const classes = useStyles();

  const { getProductsInventory } = useProductsIM();

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const getProductBySearch = useCallback(() => {
    setIsLoading(true);
    getProductsInventory(page, rowsPerPage, searchData)
      .then((result: IProductList) => {
        setProductsList(result);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    getProductBySearch();
  }, [getProductBySearch]);

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleCloseInventoryEntry = () => {
    setOpenEnterProduct(false);
  };
  const handleCloseInventoryAdjust = () => {
    setOpenAdjustment(false);
  };

  const tableCellImage = (url: string, key: number) => {
    return (
      <TableCell align="left" size="small" key={key}>
        <img src={url} alt="productImage" height="70px" width="70px" className={classes.containerImage} />
      </TableCell>
    );
  };

  const handleKeyPress = (event: { key: string }) => {
    if (event.key === 'Enter') {
      setIsLoading(true);
      getProductsInventory(page, rowsPerPage, searchData)
        .then((result: IProductList) => {
          setProductsList(result);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Fragment>
      <Grid container justify="center" style={{ marginTop: 20 }}>
        <Grid container justify="flex-start" spacing={1} style={{ marginLeft: 20 }}>
          <Grid item xs={3}>
            <MuiPickersUtilsProvider locale={esLocale} utils={DateFnsUtils}>
              <KeyboardDatePicker
                variant="inline"
                format="MM/dd/yyyy"
                inputVariant="outlined"
                margin="dense"
                autoOk={true}
                fullWidth
                onKeyPress={handleKeyPress}
                label="Fecha"
                value={searchData.fecha}
                onChange={(e) => setSearchData({ ...searchData, fecha: e })}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={3}>
            <TextField
              variant="outlined"
              margin="dense"
              fullWidth
              label="SKU"
              name="sku"
              autoComplete="sku"
              value={searchData.sku}
              onKeyPress={handleKeyPress}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setSearchData({ ...searchData, sku: e.target.value })}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              onClick={() => {
                setIsLoading(true);
                setPage(0);
                getProductsInventory(0, rowsPerPage, searchData)
                  .then((result: IProductList) => {
                    setProductsList(result);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              style={{ marginTop: 9 }}
              disabled={isLoading}
            >
              {isLoading && (
                <CircularProgress size={24} style={{ marginRight: 10, width: 16, height: 16, position: 'relative' }} />
              )}
              Buscar
            </Button>
            <Button
              onClick={() => {
                setIsLoading(true);
                getProductsInventory(1, 1000000, searchData, true)
                  .then((result) => {
                    download(result.base64, 'inventario');
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              style={{ marginLeft: 9, marginTop: 9 }}
              disabled={isLoading}
            >
              {isLoading && (
                <CircularProgress size={24} style={{ marginRight: 10, width: 16, height: 16, position: 'relative' }} />
              )}
              Exportar
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Table style={{ minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <Fragment>
              <TableCell align="center" size="small" colSpan={2}>
                Nombre
              </TableCell>
              <TableCell align="left">Tipo</TableCell>
              <TableCell align="left">SKU</TableCell>
              <TableCell align="left">Cantidad</TableCell>
              <TableCell align="center" colSpan={4}>
                Acciones
              </TableCell>
            </Fragment>
          </TableRow>
        </TableHead>
        <TableBody>
          {productsList.products.map((productInfo: IProducts) => (
            <Fragment key={`${productInfo.productId}rz`}>
              {productInfo.type === 'simple' ? (
                <TableRow key={`${productInfo.productId}r1`}>
                  {productInfo.galeryImages.map((image: any) => (
                    <Fragment key={`${image.url}rx`}>
                      {image.main ? (
                        <Fragment>
                          {image.url
                            ? tableCellImage(image.url, productInfo.productId)
                            : tableCellImage(productImage, productInfo.productId)}
                        </Fragment>
                      ) : null}
                    </Fragment>
                  ))}
                  <TableCell className={classes.tablecell} align="left" size="small">
                    {productInfo.name}
                  </TableCell>
                  <TableCell className={classes.tablecell} align="left" size="small">
                    {productInfo.type}
                  </TableCell>
                  <TableCell className={classes.tablecell} align="left" size="small">
                    {productInfo.sku}
                  </TableCell>
                  <TableCell className={classes.tablecell} align="left" size="small">
                    {productInfo.stock}
                  </TableCell>
                  <TableCell className={classes.tablecell} align="right" size="small" colSpan={3}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => {
                        handleOnClickEditProduct(String(productInfo.productId));
                      }}
                    >
                      Editar
                    </Button>
                  </TableCell>
                  <TableCell align="left" size="small">
                    <IMInventoryMenu
                      parentId={0}
                      setParentId={setParentId}
                      productId={productInfo.productId}
                      setOpenEnterProduct={setOpenEnterProduct}
                      setOpenAdjustment={setOpenAdjustment}
                      setProductId={setProductId}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <Fragment>
                  <TableRow key={`${productInfo.productId}r2`}>
                    {productInfo.galeryImages.map((image: any, index: number) => (
                      <Fragment key={`${image.url}+${index}`}>
                        {image.main ? (
                          <Fragment key={`${image.url}+${index}`}>
                            {image.url
                              ? tableCellImage(image.url, Number(image.url))
                              : tableCellImage(productImage, Number(image.url))}
                          </Fragment>
                        ) : null}
                      </Fragment>
                    ))}

                    <TableCell className={classes.tablecell} align="left" size="small">
                      {productInfo.name}
                    </TableCell>
                    <TableCell className={classes.tablecell} align="left" size="small">
                      {productInfo.type}
                    </TableCell>
                    <TableCell className={classes.tablecell} align="left" size="small">
                      {productInfo.sku}
                    </TableCell>
                  </TableRow>
                  {productInfo.variation.map((variantioninfo: Variation, index: number) => {
                    const productVariationId = variantioninfo.productId;
                    const parentVariationId = variantioninfo.parentId;
                    return (
                      <Fragment key={`${productVariationId}+${index}`}>
                        <TableCell size="small" rowSpan={2} />
                        <TableRow key={`${variantioninfo.productId}r3`}>
                          {variantioninfo.galeryImages.map((image: any) => (
                            <Fragment key={`${image.url}+${index}`}>
                              {image.main ? (
                                <Fragment key={`${image.url}+${index}`}>
                                  {image.url
                                    ? tableCellImage(image.url, image.url)
                                    : tableCellImage(productImage, image.url)}
                                </Fragment>
                              ) : null}
                            </Fragment>
                          ))}
                          <TableCell className={classes.tablecell} align="left">
                            {variantioninfo.name}
                          </TableCell>
                          <TableCell className={classes.tablecell} align="left" size="small">
                            {variantioninfo.sku}
                          </TableCell>
                          <TableCell className={classes.tablecell} align="left" size="small">
                            {variantioninfo.stock}
                          </TableCell>
                          <TableCell align="left" size="small">
                            <Button
                              variant="outlined"
                              size="small"
                              color="primary"
                              onClick={() => {
                                setParentId(parentVariationId);
                                handleOnClickEditProduct(String(variantioninfo.productId));
                              }}
                            >
                              Editar
                            </Button>
                          </TableCell>
                          <TableCell>
                            <IMInventoryMenu
                              productId={productVariationId}
                              parentId={parentVariationId}
                              setParentId={setParentId}
                              setOpenEnterProduct={setOpenEnterProduct}
                              setOpenAdjustment={setOpenAdjustment}
                              setProductId={setProductId}
                            />
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </Fragment>
              )}
            </Fragment>
          ))}
          {productsList.products.length === 0 ? (
            <TableCell className={classes.tablecell} align="center" colSpan={6}>
              <Typography variant="subtitle1" color="initial">
                No hay registros
              </Typography>
            </TableCell>
          ) : null}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[2, 5, 10, 25, 50]}
        component="div"
        count={productsList.total}
        rowsPerPage={rowsPerPage}
        page={page}
        labelDisplayedRows={({ from, to, count }) => {
          return `${from}-${to} de ${count} `;
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        labelRowsPerPage="filas por pagina"
      />

      <ModalContainer title="Entrada" open={openEnterProduct} onClose={handleCloseInventoryEntry}>
        <IMInventoryEntryModal
          open={openEnterProduct}
          getAllProducts={getAllProducts}
          productId={productId}
          parentId={parentId}
          onClose={handleCloseInventoryEntry}
        />
      </ModalContainer>
      <ModalContainer title="Ajuste" open={openAdjustment} onClose={handleCloseInventoryAdjust}>
        <IMAdjustmentModal
          open={openAdjustment}
          getAllProducts={getAllProducts}
          productId={productId}
          parentId={parentId}
          onClose={handleCloseInventoryAdjust}
        />
      </ModalContainer>
    </Fragment>
  );
}
